'use client';

import { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { toast } from 'sonner';
import SeoHead from '@/components/common/SeoHead';
import { Button } from '@/components/ui/button';
import {
  getSafeImageUrl,
  sanitizeDescription,
  sanitizeTitle,
} from '@/utils/shareMetadata';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import InteractivePopover from '@/components/common/InteractivePopover';
import ImageCarousel from '@/components/common/ImageCarousel';
import type { HotelFromItinerary } from '@/components/packages/itinerary/ItineraryInfoPopover';

const getShareBaseUrl = (): string => {
  const raw =
    process.env.NEXT_PUBLIC_FRONTEND_URL?.trim() ||
    process.env.NEXT_PUBLIC_APP_URL?.trim() ||
    '';
  if (!raw) return '';
  return raw.replace(/\/$/, '');
};

interface BookingCustomerDraft {
  userName: string;
  userEmail: string;
  userMobile: string;
}

interface PackageBookingFormState {
  packageTitle: string;
  bannerImageUrl: string;
  itineraryStatus: string;
  customers: BookingCustomerDraft[];
  tripOverview: string;
  shortItinerary: string;
  numberOfAdults: string;
  numberOfChildren: string;
  cost: string;
  detailedItinerary: string;
  inclusions: string;
  exclusions: string;
  documentsRequired: string;
  thingsToBeNoted: string;
  paymentTerms: string;
  accountDetails: string;
  possibleAdditionalCosts: string;
  locationPhotos: string;
}

interface PackageBookingPreviewClientPageProps {
  bookingId: string;
  booking: unknown;
  packageData?: unknown;
  packageSlug?: string;
}

const splitLines = (value: string): string[] =>
  value
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);

const toStringValue = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (value == null) return '';
  return String(value);
};

const toObject = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

interface AgentContactFields {
  name: string;
  phone: string;
  email: string;
}

const resolveAgentContact = (
  ...sources: Array<Record<string, unknown>>
): AgentContactFields => {
  for (const source of sources) {
    const nested =
      toObject(source.agentContact) ||
      toObject(source.agent) ||
      toObject(source.travelAgent);
    if (Object.keys(nested).length > 0) {
      return {
        name:
          toStringValue(nested.name) ||
          toStringValue(nested.agentName) ||
          toStringValue(nested.fullName),
        phone:
          toStringValue(nested.phone) ||
          toStringValue(nested.mobile) ||
          toStringValue(nested.phoneNumber),
        email: toStringValue(nested.email),
      };
    }
    const name =
      toStringValue(source.agentName) || toStringValue(source.agentContactName);
    const phone =
      toStringValue(source.agentPhone) ||
      toStringValue(source.agentMobile) ||
      toStringValue(source.agentContactPhone);
    const email =
      toStringValue(source.agentEmail) ||
      toStringValue(source.agentContactEmail);
    if (name || phone || email) {
      return { name, phone, email };
    }
  }
  return { name: '', phone: '', email: '' };
};

const toTextList = (value: unknown): string => {
  if (!Array.isArray(value)) return '';
  return value
    .map(item => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object') {
        const obj = item as Record<string, unknown>;
        const primary =
          toStringValue(obj.title) ||
          toStringValue(obj.name) ||
          toStringValue(obj.label) ||
          toStringValue(obj.text);
        const secondary =
          toStringValue(obj.description) ||
          toStringValue(obj.value) ||
          toStringValue(obj.note);
        if (primary && secondary) return `${primary}: ${secondary}`;
        if (primary) return primary;
        if (secondary) return secondary;
      }
      return '';
    })
    .filter(Boolean)
    .join('\n');
};

const normalizeListField = (primary: unknown, fallback: unknown): string => {
  const primaryText = Array.isArray(primary)
    ? toTextList(primary)
    : toStringValue(primary);
  const fallbackText = Array.isArray(fallback)
    ? toTextList(fallback)
    : toStringValue(fallback);
  const hasBrokenObjectString = (value: string) =>
    value.includes('[object Object]');
  if (primaryText && !hasBrokenObjectString(primaryText)) return primaryText;
  if (fallbackText && !hasBrokenObjectString(fallbackText)) return fallbackText;
  return primaryText || fallbackText || '';
};

const toRichHtmlFromText = (value: string): string => {
  const trimmed = value.trim();
  if (trimmed && /<\s*(p|ul|ol|li|div|br)\b/i.test(trimmed)) {
    return trimmed;
  }
  const lines = splitLines(value);
  if (lines.length === 0) return '<p></p>';
  const list = lines
    .map(line => `<li>${line.replace(/[<>]/g, '')}</li>`)
    .join('');
  return `<ul>${list}</ul>`;
};

const formatDateLabel = (date: Date): string =>
  date.toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

const ITINERARY_PIPE_SEP = ' || ';

const getItineraryStatusBadgeClass = (status: string): string => {
  const normalized = status.trim().toLowerCase();
  if (normalized === 'confirmed' || normalized === 'finalized') {
    return 'bg-emerald-100 text-emerald-800 border-emerald-200';
  }
  if (normalized === 'pending') {
    return 'bg-amber-100 text-amber-800 border-amber-200';
  }
  if (normalized === 'draft') {
    return 'bg-slate-100 text-slate-700 border-slate-200';
  }
  return 'bg-blue-100 text-blue-800 border-blue-200';
};

const resolveHotelIdString = (hotelId: unknown): string | undefined => {
  if (typeof hotelId === 'string' && hotelId.trim()) return hotelId.trim();
  if (hotelId && typeof hotelId === 'object') {
    const obj = hotelId as Record<string, unknown>;
    return (
      toStringValue(obj._id) ||
      toStringValue(obj.uid) ||
      toStringValue(obj.id) ||
      undefined
    );
  }
  return undefined;
};

const resolveHotelFromItinerary = (
  itinerary: Record<string, unknown>
): HotelFromItinerary | undefined => {
  const hotelId = itinerary.hotelId;
  if (hotelId && typeof hotelId === 'object') {
    const hotel = hotelId as Record<string, unknown>;
    if (hotel._id != null || hotel.name != null) {
      return hotelId as HotelFromItinerary;
    }
  }
  return undefined;
};

const resolveStayName = (
  itinerary: Record<string, unknown>,
  hotels: Array<Record<string, unknown>>,
  hotelFromItinerary?: HotelFromItinerary
): string => {
  const fromFields =
    toStringValue(itinerary.stay) ||
    toStringValue(itinerary.stayName) ||
    toStringValue(itinerary.hotelName) ||
    toStringValue(hotelFromItinerary?.name);
  if (fromFields) return fromFields;

  const hotelIdStr =
    resolveHotelIdString(itinerary.hotelId) ||
    resolveHotelIdString(itinerary.stayId);
  if (!hotelIdStr) return '';

  const matchedHotel = hotels.find(h => {
    const id =
      (h.id as string | undefined) ||
      (h._id as string | undefined) ||
      (h.uid as string | undefined);
    return id && id === hotelIdStr;
  });
  return toStringValue(matchedHotel?.name);
};

const resolveStayPopoverHotel = (
  hotelFromItinerary: HotelFromItinerary | undefined,
  matchedHotel: Record<string, unknown> | null | undefined
): (HotelFromItinerary & Record<string, unknown>) | undefined => {
  if (hotelFromItinerary && Object.keys(hotelFromItinerary).length > 0) {
    if (!matchedHotel)
      return hotelFromItinerary as HotelFromItinerary & Record<string, unknown>;
    return {
      ...matchedHotel,
      ...(hotelFromItinerary as Record<string, unknown>),
    };
  }
  if (!matchedHotel) return undefined;
  return {
    _id:
      toStringValue(matchedHotel._id) ||
      toStringValue(matchedHotel.id) ||
      toStringValue(matchedHotel.uid),
    name: toStringValue(matchedHotel.name),
    type: toStringValue(matchedHotel.type),
    address: toStringValue(matchedHotel.address),
    location: toStringValue(matchedHotel.location),
    description: toStringValue(matchedHotel.description),
    imageUrl: Array.isArray(matchedHotel.imageUrl)
      ? (matchedHotel.imageUrl as string[])
      : [],
    amenities: Array.isArray(matchedHotel.amenities)
      ? (matchedHotel.amenities as string[])
      : [],
  };
};

const getHotelImagesForPreview = (
  hotel: (HotelFromItinerary & Record<string, unknown>) | undefined
): string[] => {
  if (!hotel) return ['/assets/images/No-Image.jpg'];
  const seen = new Set<string>();
  const images: string[] = [];

  const pushUrl = (url: unknown) => {
    if (typeof url !== 'string') return;
    const trimmed = url.trim();
    if (!trimmed || seen.has(trimmed)) return;
    seen.add(trimmed);
    images.push(trimmed);
  };

  const bookingMediaObj = toObject(hotel.bookingMedia);
  if (Array.isArray(bookingMediaObj.imageUrl)) {
    bookingMediaObj.imageUrl.forEach(pushUrl);
  }
  if (Array.isArray(hotel.media)) {
    hotel.media.forEach(item => {
      if (item?.format === 'image' && item?.url) {
        pushUrl(item.url);
      }
    });
  }
  if (Array.isArray(hotel.imageUrl)) {
    hotel.imageUrl.forEach(pushUrl);
  }

  return images.length > 0 ? images : ['/assets/images/No-Image.jpg'];
};

const getHotelLocationText = (
  hotel: (HotelFromItinerary & Record<string, unknown>) | undefined
): string => {
  if (!hotel) return '';
  const fallbackLocation = [hotel.city, hotel.state, hotel.country]
    .map(part => toStringValue(part).trim())
    .filter(Boolean)
    .join(', ');
  return toStringValue(hotel.location).trim() || fallbackLocation;
};

const getHotelAddressText = (
  hotel: (HotelFromItinerary & Record<string, unknown>) | undefined,
  locationText: string
): string => {
  if (!hotel) return '';
  const addressText = toStringValue(hotel.address).trim();
  if (!addressText) return '';
  return addressText.toLowerCase() === locationText.toLowerCase()
    ? ''
    : addressText;
};

const getHotelAmenities = (
  hotel: (HotelFromItinerary & Record<string, unknown>) | undefined
): string[] => {
  if (!hotel || !Array.isArray(hotel.amenities)) return [];
  return hotel.amenities
    .flatMap(item =>
      typeof item === 'string' && item.includes(',')
        ? item
            .split(',')
            .map(v => v.trim())
            .filter(Boolean)
        : [toStringValue(item).trim()].filter(Boolean)
    )
    .filter((value, index, arr) => arr.indexOf(value) === index);
};

interface ItineraryHighlightLocation {
  name?: string;
  city?: string;
  state?: string;
}

interface ItineraryHighlightItem {
  _id?: string;
  title?: string;
  imageUrl?: string[];
  locations?: ItineraryHighlightLocation | string;
  description?: string;
  isIncluded?: boolean;
}

const resolveHighlightId = (value: unknown): string => {
  if (typeof value === 'string') return value.trim();
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    return (
      toStringValue(obj._id) ||
      toStringValue(obj.id) ||
      toStringValue(obj.uid) ||
      ''
    );
  }
  return '';
};

const toHighlightItem = (
  value: unknown,
  fallbackById: Map<string, ItineraryHighlightItem>
): ItineraryHighlightItem | null => {
  const id = resolveHighlightId(value);
  const fallback = id ? fallbackById.get(id) : undefined;
  const obj = value && typeof value === 'object' ? toObject(value) : {};
  if (!id && Object.keys(obj).length === 0 && !fallback) return null;
  return {
    _id: id || fallback?._id,
    title: toStringValue(obj.title) || fallback?.title || '',
    imageUrl: Array.isArray(obj.imageUrl)
      ? (obj.imageUrl as string[])
      : Array.isArray(fallback?.imageUrl)
        ? fallback.imageUrl
        : [],
    locations:
      (obj.locations as ItineraryHighlightLocation | string | undefined) ??
      fallback?.locations,
    description:
      toStringValue(obj.description) ||
      toStringValue(obj.longDescription) ||
      fallback?.description ||
      '',
    isIncluded:
      typeof obj.isIncluded === 'boolean'
        ? obj.isIncluded
        : fallback?.isIncluded,
  };
};

const getHighlightLocationText = (
  location: ItineraryHighlightLocation | string | undefined
): string => {
  if (!location) return '';
  if (typeof location === 'string') return location.trim();
  return [location.city, location.state, location.name]
    .filter(Boolean)
    .join(', ');
};

const getHighlightStatus = (
  isIncluded: boolean | undefined
): { label: string; className: string } => {
  if (isIncluded === true) {
    return {
      label: 'Included',
      className: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    };
  }
  if (isIncluded === false) {
    return {
      label: 'Excluded',
      className: 'bg-rose-100 text-rose-800 border-rose-200',
    };
  }
  return {
    label: 'Status N/A',
    className: 'bg-slate-100 text-slate-700 border-slate-200',
  };
};

interface ItineraryPreviewRow {
  day: string;
  date: string;
  stay: string;
  transfer: string;
  details: string;
}

const normalizeItineraryPlanForStorage = (value: string): string =>
  value.replace(/\r\n/g, '\n').replace(/\n+/g, ' ').replace(/\s+/g, ' ').trim();

const resolveItineraryPreviewRow = (
  itinerary: Record<string, unknown>,
  index: number,
  hotels: Array<Record<string, unknown>>,
  bookingStartDate: Date | null
): ItineraryPreviewRow => {
  const plan = toStringValue(itinerary.description);
  const itineraryDateRaw =
    itinerary.date || itinerary.dayDate || itinerary.startDate || null;
  const computedFromField =
    itineraryDateRaw != null && String(itineraryDateRaw).trim() !== ''
      ? formatDateLabel(new Date(String(itineraryDateRaw)))
      : '';
  const computedFromBooking =
    !computedFromField && bookingStartDate
      ? formatDateLabel(
          new Date(bookingStartDate.getTime() + index * 24 * 60 * 60 * 1000)
        )
      : '';
  const dateCell = computedFromField || computedFromBooking;
  const hotelFromItinerary = resolveHotelFromItinerary(itinerary);
  const stay = resolveStayName(itinerary, hotels, hotelFromItinerary);
  const transfer =
    toStringValue(itinerary.transfer) ||
    toStringValue(itinerary.transport) ||
    toStringValue(itinerary.transferDetails) ||
    '';

  return {
    day: `Day ${index + 1}`,
    date: dateCell,
    stay,
    transfer,
    details: plan || '—',
  };
};

const formatItineraryLine = (
  day: string,
  date: string,
  stay: string,
  transfer: string,
  plan: string
): string =>
  `${day}${ITINERARY_PIPE_SEP}${date}${ITINERARY_PIPE_SEP}${stay}${ITINERARY_PIPE_SEP}${transfer}${ITINERARY_PIPE_SEP}${plan}`;

const toItineraryRowsFromItineraries = (
  itineraries: unknown,
  hotels: Array<Record<string, unknown>> = [],
  bookingStartDate: Date | null = null
): ItineraryPreviewRow[] => {
  if (!Array.isArray(itineraries)) return [];
  return itineraries
    .filter(
      (item): item is Record<string, unknown> =>
        item != null && typeof item === 'object'
    )
    .map((itinerary, index) =>
      resolveItineraryPreviewRow(itinerary, index, hotels, bookingStartDate)
    );
};

const toItineraryText = (
  itineraries: unknown,
  hotels: Array<Record<string, unknown>> = [],
  bookingStartDate: Date | null = null
): string => {
  return toItineraryRowsFromItineraries(itineraries, hotels, bookingStartDate)
    .map(row => {
      const planCell =
        row.details === '—'
          ? '—'
          : normalizeItineraryPlanForStorage(row.details) || '—';
      return formatItineraryLine(
        row.day,
        row.date,
        row.stay,
        row.transfer,
        planCell
      );
    })
    .filter(Boolean)
    .join('\n');
};

const splitItineraryRecords = (value: string): string[] => {
  const lines = value.replace(/\r\n/g, '\n').split('\n');
  const records: string[] = [];
  let current = '';

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    const isNewRecord =
      /^Day\s+\d+/i.test(trimmed) && trimmed.includes(ITINERARY_PIPE_SEP);

    if (isNewRecord && current) {
      records.push(current);
      current = trimmed;
    } else if (isNewRecord) {
      current = trimmed;
    } else if (current) {
      current = `${current} ${trimmed}`;
    } else {
      current = trimmed;
    }
  }

  if (current) records.push(current);
  return records;
};

const parseItineraryLine = (
  line: string,
  index: number
): {
  day: string;
  date: string;
  stay: string;
  transfer: string;
  plan: string;
} => {
  if (line.includes(ITINERARY_PIPE_SEP)) {
    const parts = line.split(ITINERARY_PIPE_SEP);
    const day = (parts[0] ?? '').trim() || `Day ${index + 1}`;
    const date = (parts[1] ?? '').trim();
    const stay = (parts[2] ?? '').trim();
    const hasTransferColumn = parts.length >= 5;
    const transfer = hasTransferColumn ? (parts[3] ?? '').trim() : '';
    const plan =
      (hasTransferColumn
        ? parts.slice(4).join(ITINERARY_PIPE_SEP)
        : parts.slice(3).join(ITINERARY_PIPE_SEP)
      ).trim() || '—';
    return { day, date, stay, transfer, plan };
  }
  const [left, ...rest] = line.split(':');
  const plan = rest.join(':').trim() || '—';
  const day = left.trim() || `Day ${index + 1}`;
  return { day, date: '', stay: '', transfer: '', plan };
};

const toItineraryRows = (value: string): ItineraryPreviewRow[] => {
  return splitItineraryRecords(value).map((record, index) => {
    const parsed = parseItineraryLine(record, index);
    return {
      day: `Day ${index + 1}`,
      date: parsed.date,
      stay: parsed.stay,
      transfer: parsed.transfer,
      details: parsed.plan,
    };
  });
};

const looksLikeHtml = (value: string): boolean => {
  const t = value.trim();
  if (!t) return false;
  if (t.startsWith('<')) return true;
  return /<\s*(p|ul|ol|li|div|br|table|thead|tbody|tr|td|th|strong|em|b|i)\b/i.test(
    t
  );
};

const paymentTermsTableProse =
  '[&_table]:w-full [&_table]:border-collapse [&_table]:text-sm [&_th]:border [&_td]:border [&_th]:border-gray-200 [&_td]:border-gray-200 [&_th]:bg-gray-50 [&_th]:px-4 [&_td]:px-4 [&_th]:py-2.5 [&_td]:py-2.5 [&_th]:text-left [&_td]:text-left [&_th]:font-semibold [&_th]:text-gray-800 [&_td]:text-gray-800';

const parsePlainPaymentTerms = (
  raw: string
): { rows: Array<{ left: string; right: string }>; notes: string[] } => {
  const lines = raw.split(/\r?\n/).map(l => l.trim());
  const rows: Array<{ left: string; right: string }> = [];
  const notes: string[] = [];
  for (const line of lines) {
    if (!line) continue;
    if (line.includes('|')) {
      const parts = line.split(/\s*\|\s*/).map(p => p.trim());
      if (parts.length === 2) {
        const [l, r] = parts;
        if (/^payment date$/i.test(l) && /amount/i.test(r)) continue;
        rows.push({ left: l, right: r });
        continue;
      }
    }
    if (line.includes('\t')) {
      const [l, ...rest] = line.split('\t');
      const r = rest.join('\t').trim();
      rows.push({ left: l.trim(), right: r });
      continue;
    }
    notes.push(line);
  }
  return { rows, notes };
};

function PaymentTermsNotes({ notes }: { notes: string[] }) {
  const notesText = notes.join('\n');
  if (looksLikeHtml(notesText)) {
    return (
      <div
        className='py-2 text-sm text-gray-800 prose prose-sm max-w-none leading-5'
        dangerouslySetInnerHTML={{ __html: notesText }}
      />
    );
  }
  return (
    <div className='space-y-3 py-2 text-sm text-gray-800'>
      {notes.map((note, i) => (
        <p key={`${note}-${i}`} className='leading-5 whitespace-pre-wrap'>
          {note}
        </p>
      ))}
    </div>
  );
}

function PaymentTermsColumn({
  content,
  totalAmount,
}: {
  content: string;
  totalAmount: number;
}) {
  const trimmed = content.trim();
  if (!trimmed) {
    return <p className='text-sm text-gray-500'>—</p>;
  }
  const { rows, notes } = parsePlainPaymentTerms(trimmed);
  const formatAmountWithPercentage = (value: string) => {
    const amount = Number.parseFloat(value.replace(/[^\d.]/g, ''));
    if (!Number.isFinite(amount) || totalAmount <= 0) {
      return value;
    }
    const percentage = (amount / totalAmount) * 100;
    const roundedPercentage = Number.parseFloat(percentage.toFixed(2));
    return `${value} / ${roundedPercentage}%`;
  };
  if (rows.length > 0) {
    return (
      <div className='space-y-4'>
        <div className='overflow-hidden rounded-md border border-gray-200'>
          <table className='w-full border-collapse text-sm'>
            <thead>
              <tr className='bg-gray-50'>
                <th className='border-b border-gray-200 px-4 py-2.5 text-left font-semibold text-gray-800'>
                  Payment Date
                </th>
                <th className='border-b border-gray-200 px-4 py-2.5 text-left font-semibold text-gray-800'>
                  Amount / Percentage paid
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.left}-${i}`} className='border-t border-gray-200'>
                  <td className='px-4 py-2.5 align-top text-gray-900'>
                    {r.left}
                  </td>
                  <td className='px-4 py-2.5 align-top text-gray-800'>
                    {formatAmountWithPercentage(r.right)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {notes.length > 0 && <PaymentTermsNotes notes={notes} />}
      </div>
    );
  }
  if (looksLikeHtml(trimmed)) {
    return (
      <div
        className={`text-gray-800 leading-7 prose prose-sm max-w-none ${paymentTermsTableProse}`}
        dangerouslySetInnerHTML={{ __html: trimmed }}
      />
    );
  }
  return (
    <div className='text-sm leading-7 text-gray-800 whitespace-pre-wrap'>
      {trimmed}
    </div>
  );
}

const parsePlainAccountDetails = (
  raw: string
): { intro: string; box: string; footer: string } => {
  const lines = raw.replace(/\r\n/g, '\n').split('\n');
  const firstBank = lines.findIndex(l =>
    /account\s+holder|a\/c\s*no|ac\s*no|ifsc|upi\s*id/i.test(l.trim())
  );
  if (firstBank === -1) {
    return { intro: raw.trim(), box: '', footer: '' };
  }
  const intro = lines.slice(0, firstBank).join('\n').trim();
  let footerStart = lines.length;
  for (let i = firstBank; i < lines.length; i++) {
    if (/^for\s+payments\b/i.test(lines[i].trim())) {
      footerStart = i;
      break;
    }
  }
  const box = lines.slice(firstBank, footerStart).join('\n').trim();
  const footer = lines.slice(footerStart).join('\n').trim();
  return { intro, box, footer };
};

function AccountDetailsColumn({ content }: { content: string }) {
  const trimmed = content.trim();
  if (!trimmed) {
    return <p className='text-sm text-gray-500'>—</p>;
  }
  if (looksLikeHtml(trimmed)) {
    return (
      <div
        className={`text-gray-800 leading-7 prose prose-sm max-w-none ${paymentTermsTableProse} [&_pre]:rounded-lg [&_pre]:bg-gray-100 [&_pre]:px-4 [&_pre]:py-4 [&_pre]:font-mono [&_pre]:text-[13px]`}
        dangerouslySetInnerHTML={{ __html: trimmed }}
      />
    );
  }
  const { intro, box, footer } = parsePlainAccountDetails(trimmed);
  return (
    <div className='space-y-4 text-sm leading-7 text-gray-800'>
      {intro ? <p className='whitespace-pre-wrap'>{intro}</p> : null}
      {box ? (
        <div className='rounded-lg bg-gray-100 px-4 py-4 font-mono text-[13px] leading-6 text-gray-900 whitespace-pre-wrap'>
          {box}
        </div>
      ) : null}
      {footer ? <p className='whitespace-pre-wrap'>{footer}</p> : null}
    </div>
  );
}

export default function PackageBookingPreviewClientPage({
  bookingId,
  booking,
  packageData,
  packageSlug: packageSlugProp,
}: PackageBookingPreviewClientPageProps) {
  const router = useRouter();
  useEffect(() => {
    console.log('[PackageBookingPreviewClientPage] received booking data:', {
      bookingId,
      booking,
      packageData,
    });
  }, [bookingId, booking, packageData]);

  const packageDataObject = toObject(packageData);
  const bookingFromPackageData = toObject(packageDataObject.booking);
  const bookingObject = {
    ...bookingFromPackageData,
    ...toObject(booking),
  };
  const packageDetails = toObject(
    bookingObject.packageDetails ?? packageDataObject
  );
  const users = Array.isArray(bookingObject.users)
    ? (bookingObject.users as unknown[])
    : Array.isArray(bookingFromPackageData.users)
      ? (bookingFromPackageData.users as unknown[])
      : [];
  const fallbackUser = toObject(
    bookingObject.user ?? bookingFromPackageData.user
  );
  const priceDetails = toObject(
    bookingObject.priceDetails ??
      bookingFromPackageData.priceDetails ??
      packageDetails.priceDetails
  );

  const initialState = useMemo<PackageBookingFormState>(() => {
    const hotels = Array.isArray(packageDetails.hotels)
      ? (packageDetails.hotels as Array<Record<string, unknown>>)
      : [];
    const bookingStartDateRaw =
      bookingObject.bookingStartDate ?? bookingFromPackageData.bookingStartDate;
    const bookingStartDate = bookingStartDateRaw
      ? new Date(String(bookingStartDateRaw))
      : null;
    return {
      packageTitle: toStringValue(packageDetails.title ?? packageDetails.name),
      bannerImageUrl: toStringValue(
        packageDetails.bannerImageUrl ??
          packageDetails.bannerImage ??
          packageDetails.heroImage
      ),
      itineraryStatus: toStringValue(packageDetails.itineraryStatus ?? 'Draft'),
      customers:
        users.length > 0
          ? users.map(user => {
              const userObj = toObject(user);
              return {
                userName: toStringValue(userObj.userName),
                userEmail: toStringValue(userObj.userEmail),
                userMobile: toStringValue(userObj.userMobile),
              };
            })
          : [
              {
                userName: toStringValue(fallbackUser.userName),
                userEmail: toStringValue(fallbackUser.userEmail),
                userMobile: toStringValue(fallbackUser.userMobile),
              },
            ],
      tripOverview: toStringValue(
        packageDetails.tripOverview ?? packageDetails.shortDescription
      ),
      shortItinerary: toStringValue(
        packageDetails.shortItinerary ?? packageDetails.description
      ),
      numberOfAdults: String(bookingObject.numberOfAdults ?? 0),
      numberOfChildren: String(bookingObject.numberOfChildren ?? 0),
      cost: toStringValue(packageDetails.cost ?? priceDetails.totalCost),
      detailedItinerary: toStringValue(
        packageDetails.detailedItinerary ??
          toItineraryText(packageDetails.itineraries, hotels, bookingStartDate)
      ),
      inclusions: toRichHtmlFromText(
        normalizeListField(packageDetails.inclusions, packageDetails.includes)
      ),
      exclusions: toRichHtmlFromText(
        normalizeListField(packageDetails.exclusions, packageDetails.excludes)
      ),
      documentsRequired: toStringValue(packageDetails.documentsRequired),
      thingsToBeNoted: toStringValue(packageDetails.thingsToBeNoted),
      paymentTerms: toStringValue(
        packageDetails.paymentTerms ??
          packageDetails.termsAndConditions ??
          packageDetails.terms
      ),
      accountDetails: toStringValue(
        packageDetails.accountDetails ??
          packageDetails.bankDetails ??
          packageDetails.bankingInfo
      ),
      possibleAdditionalCosts: toStringValue(
        packageDetails.possibleAdditionalCosts
      ),
      locationPhotos: Array.isArray(packageDetails.imageUrl)
        ? (packageDetails.imageUrl as string[]).join('\n')
        : toStringValue(packageDetails.locationPhotos),
    };
  }, [
    bookingFromPackageData,
    bookingObject,
    fallbackUser,
    packageDetails,
    priceDetails,
    users,
  ]);

  const [form] = useState<PackageBookingFormState>(initialState);
  const [shareUrlDialogOpen, setShareUrlDialogOpen] = useState(false);
  const [shareUrlSnapshot, setShareUrlSnapshot] = useState('');

  const previewSections: Array<{ title: string; content: string }> = [
    { title: 'Trip Overview', content: form.tripOverview },
    { title: 'Travel Story', content: form.shortItinerary },
    { title: 'Detailed Itinerary', content: form.detailedItinerary },
    { title: 'Inclusions', content: form.inclusions },
    { title: 'Exclusions', content: form.exclusions },
    { title: 'Documents Required', content: form.documentsRequired },
    { title: 'Things to be Noted', content: form.thingsToBeNoted },
    { title: 'Payment Terms', content: form.paymentTerms },
    { title: 'Account details', content: form.accountDetails },
    {
      title: 'Possible Additional Costs',
      content: form.possibleAdditionalCosts,
    },
  ];

  const locationPhotoUrls = splitLines(form.locationPhotos);
  const bannerImageUrl = form.bannerImageUrl.trim() || locationPhotoUrls[0];
  const sourceItineraries = Array.isArray(packageDetails.itineraries)
    ? (packageDetails.itineraries as Array<Record<string, unknown>>)
    : [];
  const hotels = Array.isArray(packageDetails.hotels)
    ? (packageDetails.hotels as Array<Record<string, unknown>>)
    : [];
  const bookingStartDateRaw =
    bookingObject.bookingStartDate ?? bookingFromPackageData.bookingStartDate;
  const bookingStartDate = bookingStartDateRaw
    ? new Date(String(bookingStartDateRaw))
    : null;
  const itineraryRows = useMemo(() => {
    if (sourceItineraries.length > 0) {
      return toItineraryRowsFromItineraries(
        sourceItineraries,
        hotels,
        bookingStartDate
      );
    }
    return toItineraryRows(form.detailedItinerary);
  }, [sourceItineraries, hotels, bookingStartDate, form.detailedItinerary]);
  const highlights = Array.isArray(packageDetails.highlights)
    ? (packageDetails.highlights as unknown[])
    : [];
  const highlightsById = useMemo(() => {
    const map = new Map<string, ItineraryHighlightItem>();
    highlights.forEach(item => {
      const id = resolveHighlightId(item);
      if (!id) return;
      const normalized = toHighlightItem(item, new Map());
      if (normalized) {
        map.set(id, normalized);
      }
    });
    return map;
  }, [highlights]);
  const adultsCount = Number.parseInt(form.numberOfAdults || '0', 10);
  const childrenCount = Number.parseInt(form.numberOfChildren || '0', 10);
  const visibleAdultsCount = Number.isFinite(adultsCount)
    ? Math.max(0, adultsCount)
    : 0;
  const visibleChildrenCount = Number.isFinite(childrenCount)
    ? Math.max(0, childrenCount)
    : 0;
  const primaryGuest = form.customers[0];
  const additionalGuests = form.customers
    .slice(1)
    .filter(guest => guest.userName || guest.userEmail || guest.userMobile);
  const previewTotalCost =
    visibleAdultsCount * Number(priceDetails.pricePerAdult ?? 0) +
    visibleChildrenCount * Number(priceDetails.pricePerChild ?? 0);
  const normalizedCostText = form.cost.trim();
  const normalizedCostNumber = Number.parseFloat(
    normalizedCostText.replace(/[^\d.]/g, '')
  );
  const shouldShowCostNotes =
    normalizedCostText.length > 0 &&
    (!Number.isFinite(normalizedCostNumber) ||
      normalizedCostNumber !== previewTotalCost);
  const tripIntro = form.tripOverview.trim();
  const bookingCode = String(bookingObject.code || '').trim();
  const bookingIdentifier = bookingCode || String(bookingId).trim();
  const customerName = String(primaryGuest?.userName || '').trim();
  const slugFromRoute =
    typeof router.query.slug === 'string' ? router.query.slug.trim() : '';
  const packageSlug =
    packageSlugProp?.trim() ||
    slugFromRoute ||
    toStringValue(packageDetails.slug) ||
    toStringValue(packageDataObject.slug);
  const bookingObjectId = String(
    bookingObject._id ?? bookingObject.id ?? bookingId
  ).trim();
  const shareBaseUrl =
    getShareBaseUrl() ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  const sharePageUrl =
    shareBaseUrl && packageSlug
      ? `${shareBaseUrl}/package-details/${packageSlug}?booking=${encodeURIComponent(
          bookingObjectId
        )}`
      : '';
  const shortDescription = tripIntro;
  const seoPath = router.pathname.startsWith('/package-booking-preview')
    ? packageSlug
      ? `/package-booking-preview/${packageSlug}/${bookingId}`
      : undefined
    : packageSlug
      ? `/package-details/${packageSlug}`
      : undefined;
  const seoTitle = sanitizeTitle(
    form.packageTitle
      ? `${form.packageTitle} - Package Booking`
      : 'Package Booking'
  );
  const seoDescription = sanitizeDescription(tripIntro || form.shortItinerary);
  const seoImage = getSafeImageUrl(bannerImageUrl);

  const agentContact = useMemo(
    () =>
      resolveAgentContact(
        packageDetails,
        bookingObject,
        bookingFromPackageData,
        packageDataObject
      ),
    [packageDetails, bookingObject, bookingFromPackageData, packageDataObject]
  );
  const hasAgentContact =
    agentContact.name.trim().length > 0 ||
    agentContact.phone.trim().length > 0 ||
    agentContact.email.trim().length > 0;

  const shareWhatsappUrl = `https://wa.me/?text=${encodeURIComponent(
    [
      customerName
        ? `Hi ${customerName}, here are your booking details.`
        : 'Hi, here are your booking details.',
      '',
      form.packageTitle || '',
      shortDescription || '',
      bookingIdentifier ? `Booking ID: ${bookingIdentifier}` : '',
      '',
      'View your booking:',
      sharePageUrl,
    ]
      .filter(Boolean)
      .join('\n')
  )}`;

  if (
    Object.keys(bookingObject).length === 0 &&
    Object.keys(packageDataObject).length === 0
  ) {
    return (
      <div className='min-h-screen bg-white p-10 text-red-600'>
        Failed to load package booking data
      </div>
    );
  }

  return (
    <>
      <SeoHead
        title={seoTitle}
        description={seoDescription}
        path={seoPath}
        image={seoImage}
      />
      <div className='bg-white min-h-screen'>
        {bannerImageUrl ? (
          <img
            src={bannerImageUrl}
            alt='Package banner'
            className='w-full h-[460px] object-cover'
          />
        ) : (
          <div className='w-full h-[460px] bg-gray-100' />
        )}

        <div className='w-full max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16 xl:px-20 py-10 space-y-8'>
          <div>
            <Image
              src='/assets/images/logo.svg'
              alt='Nexgo'
              width={120}
              height={36}
              className='h-8 md:h-9 w-auto'
              priority
            />
          </div>
          <div className='flex flex-wrap items-start justify-between gap-3'>
            <h1 className='text-4xl font-bold text-gray-900'>
              {form.packageTitle || 'Package Booking'}
            </h1>
            <div className='flex flex-wrap items-center gap-2'>
              <button
                type='button'
                disabled={!sharePageUrl.startsWith('http')}
                title={
                  !packageSlug
                    ? 'Package slug is required to build a share link'
                    : !sharePageUrl.startsWith('http')
                      ? 'Set NEXT_PUBLIC_FRONTEND_URL (or NEXT_PUBLIC_APP_URL) in .env for a stable share link'
                      : undefined
                }
                onClick={async () => {
                  setShareUrlSnapshot(sharePageUrl);
                  try {
                    await navigator.clipboard.writeText(sharePageUrl);
                    toast.success('Link copied to clipboard');
                    setShareUrlDialogOpen(true);
                  } catch {
                    toast.error(
                      'Could not copy automatically. Copy the link from the dialog.'
                    );
                    setShareUrlDialogOpen(true);
                  }
                }}
                className='inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
              >
                Copy link
              </button>
              <a
                href={shareWhatsappUrl}
                target='_blank'
                rel='noopener noreferrer'
                className='inline-flex items-center rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700'
              >
                Share
              </a>
            </div>
          </div>

          <section className='border rounded-md border-gray-200'>
            <div className='grid grid-cols-2 gap-0'>
              <div className='border-r border-gray-200 p-3 text-sm font-medium text-gray-600'>
                Itinerary Status
              </div>
              <div className='p-3'>
                <span
                  className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${getItineraryStatusBadgeClass(
                    form.itineraryStatus || 'Draft'
                  )}`}
                >
                  {form.itineraryStatus || 'Draft'}
                </span>
              </div>
            </div>
          </section>

          <section className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div className='border rounded-md border-gray-200'>
              <div className='px-4 py-3 border-b border-gray-200 text-sm font-semibold text-gray-900'>
                Booking Details
              </div>
              <div className='p-4 space-y-2 text-sm text-gray-800'>
                <p>
                  <span className='font-medium text-gray-600'>
                    Booking Code:
                  </span>{' '}
                  {bookingCode || '—'}
                </p>
                <p>
                  <span className='font-medium text-gray-600'>Customer:</span>{' '}
                  {primaryGuest?.userName || '—'}
                </p>
                <p>
                  <span className='font-medium text-gray-600'>Email:</span>{' '}
                  {primaryGuest?.userEmail || '—'}
                </p>
                <p>
                  <span className='font-medium text-gray-600'>Mobile:</span>{' '}
                  {primaryGuest?.userMobile || '—'}
                </p>
                {additionalGuests.length > 0 ? (
                  <div className='pt-2'>
                    <p className='font-medium text-gray-600 mb-1'>
                      Additional Customers:
                    </p>
                    <ul className='list-disc pl-5 space-y-1'>
                      {additionalGuests.map((guest, index) => (
                        <li
                          key={`${guest.userEmail}-${index}`}
                          className='text-gray-800'
                        >
                          {guest.userName || 'Unnamed'}{' '}
                          {guest.userEmail
                            ? `(${guest.userEmail}${guest.userMobile ? `, ${guest.userMobile}` : ''})`
                            : guest.userMobile
                              ? `(${guest.userMobile})`
                              : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                <p>
                  <span className='font-medium text-gray-600'>Guests:</span>{' '}
                  {visibleAdultsCount} Adults
                  {visibleChildrenCount > 0
                    ? `, ${visibleChildrenCount} Children`
                    : ''}
                </p>
              </div>
            </div>

            <div className='border rounded-md border-gray-200'>
              <div className='px-4 py-3 border-b border-gray-200 text-sm font-semibold text-gray-900'>
                Price
              </div>
              <div className='p-4 space-y-2 text-sm text-gray-800'>
                <p>
                  <span className='font-medium text-gray-600'>Per Adult:</span>{' '}
                  {toStringValue(priceDetails.currencySymbol)}
                  {Number(priceDetails.pricePerAdult ?? 0).toLocaleString()}
                </p>
                <p>
                  <span className='font-medium text-gray-600'>Per Child:</span>{' '}
                  {toStringValue(priceDetails.currencySymbol)}
                  {Number(priceDetails.pricePerChild ?? 0).toLocaleString()}
                </p>
                <p>
                  <span className='font-medium text-gray-600'>Currency:</span>{' '}
                  {toStringValue(priceDetails.currency) || '—'}
                </p>
                <p className='pt-2 text-base font-semibold text-gray-900'>
                  Total: {toStringValue(priceDetails.currencySymbol)}
                  {previewTotalCost.toLocaleString()}
                </p>
                {shouldShowCostNotes ? (
                  <p className='text-sm text-gray-700 whitespace-pre-wrap'>
                    {form.cost}
                  </p>
                ) : null}
              </div>
            </div>
          </section>

          {previewSections.flatMap(section => {
            if (section.title === 'Account details') {
              return [];
            }
            if (section.title === 'Payment Terms') {
              return [
                <section
                  key='payment-account'
                  className='grid grid-cols-1 gap-10 border-t border-gray-100 pt-8 md:grid-cols-2 md:gap-14 lg:gap-16'
                >
                  <div>
                    <h2 className='mb-3 text-xl font-semibold text-gray-900'>
                      Payment Terms
                    </h2>
                    <PaymentTermsColumn
                      content={section.content}
                      totalAmount={
                        Number.isFinite(normalizedCostNumber) &&
                        normalizedCostNumber > 0
                          ? normalizedCostNumber
                          : previewTotalCost
                      }
                    />
                  </div>
                  <div>
                    <h2 className='mb-3 text-xl font-semibold text-gray-900'>
                      Account details
                    </h2>
                    <AccountDetailsColumn content={form.accountDetails} />
                  </div>
                </section>,
              ];
            }
            return [
              <section key={section.title}>
                <h2 className='mb-2 text-xl font-semibold text-gray-900'>
                  {section.title}
                </h2>
                {section.title === 'Detailed Itinerary' ? (
                  itineraryRows.length > 0 ? (
                    <div className='w-full min-w-0 overflow-x-auto rounded-md border border-gray-200'>
                      <table className='w-full min-w-[920px] border-collapse text-sm'>
                        <thead className='bg-gray-50'>
                          <tr>
                            <th className='min-w-[72px] whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-700'>
                              Day
                            </th>
                            <th className='min-w-[100px] whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-700'>
                              Date
                            </th>
                            <th className='min-w-[120px] px-4 py-3 text-left font-semibold text-gray-700'>
                              Stay
                            </th>
                            <th className='min-w-[180px] px-4 py-3 text-left font-semibold text-gray-700'>
                              Highlights
                            </th>
                            <th className='min-w-[240px] px-4 py-3 text-left font-semibold text-gray-700'>
                              Plan
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {itineraryRows.map((row, index) => {
                            const source = sourceItineraries[index] || {};
                            const itineraryDateRaw =
                              source.date ||
                              source.dayDate ||
                              source.startDate ||
                              null;
                            const computedDate = bookingStartDate
                              ? new Date(
                                  bookingStartDate.getTime() +
                                    index * 24 * 60 * 60 * 1000
                                )
                              : null;
                            const fallbackDateLabel = itineraryDateRaw
                              ? formatDateLabel(
                                  new Date(String(itineraryDateRaw))
                                )
                              : computedDate
                                ? formatDateLabel(computedDate)
                                : '—';

                            const hotelFromItinerary =
                              resolveHotelFromItinerary(source);
                            const hotelIdStr =
                              resolveHotelIdString(source.hotelId) ||
                              resolveHotelIdString(source.stayId);
                            const matchedHotel = hotelIdStr
                              ? hotels.find(h => {
                                  const id =
                                    (h.id as string | undefined) ||
                                    (h._id as string | undefined) ||
                                    (h.uid as string | undefined);
                                  return id && id === hotelIdStr;
                                })
                              : null;
                            const fallbackStayLabel =
                              resolveStayName(
                                source,
                                hotels,
                                hotelFromItinerary
                              ) || '—';
                            const dateCell = row.date.trim()
                              ? row.date.trim()
                              : fallbackDateLabel;
                            const stayCell = row.stay.trim()
                              ? row.stay.trim()
                              : fallbackStayLabel;
                            const stayPopoverHotel = resolveStayPopoverHotel(
                              hotelFromItinerary,
                              matchedHotel
                            );
                            const itineraryHighlightsRaw = Array.isArray(
                              source.highlights
                            )
                              ? (source.highlights as unknown[])
                              : [];
                            const itineraryHighlights = itineraryHighlightsRaw
                              .map(item =>
                                toHighlightItem(item, highlightsById)
                              )
                              .filter(
                                (item): item is ItineraryHighlightItem =>
                                  item != null
                              );

                            return (
                              <tr
                                key={`${row.day}-${index}`}
                                className='border-t border-gray-200'
                              >
                                <td className='whitespace-nowrap px-4 py-3 align-top font-medium text-gray-900'>
                                  {row.day}
                                </td>
                                <td className='whitespace-nowrap px-4 py-3 align-top text-gray-800'>
                                  {dateCell}
                                </td>
                                <td className='px-4 py-3 align-top text-gray-800'>
                                  {stayPopoverHotel && stayCell !== '—'
                                    ? (() => {
                                        const hotelImages =
                                          getHotelImagesForPreview(
                                            stayPopoverHotel
                                          );
                                        const hotelName =
                                          toStringValue(
                                            stayPopoverHotel.name
                                          ).trim() || stayCell;
                                        const hotelType = toStringValue(
                                          stayPopoverHotel.type
                                        ).trim();
                                        const hotelRating = toStringValue(
                                          stayPopoverHotel.rating
                                        ).trim();
                                        const hotelCode = toStringValue(
                                          stayPopoverHotel.code
                                        ).trim();
                                        const locationText =
                                          getHotelLocationText(
                                            stayPopoverHotel
                                          );
                                        const addressText = getHotelAddressText(
                                          stayPopoverHotel,
                                          locationText
                                        );
                                        const hotelDescription = toStringValue(
                                          stayPopoverHotel.description
                                        ).trim();
                                        const amenities =
                                          getHotelAmenities(stayPopoverHotel);
                                        const displayAmenities =
                                          amenities.slice(0, 10);
                                        const remainingAmenities =
                                          amenities.length -
                                          displayAmenities.length;
                                        return (
                                          <InteractivePopover
                                            triggerClassName='inline-flex cursor-pointer'
                                            contentClassName='w-[320px] p-0 !rounded-[15px] !border-white border shadow-[0px_0px_5px_1px_rgba(0,0,0,0.15)] overflow-hidden'
                                            trigger={
                                              <span
                                                className='underline decoration-dotted underline-offset-2 hover:text-gray-900'
                                                aria-label={`${stayCell} details`}
                                              >
                                                {stayCell}
                                              </span>
                                            }
                                            content={
                                              <div className='bg-white overflow-hidden'>
                                                <div className='h-[160px] w-full overflow-hidden'>
                                                  <ImageCarousel
                                                    images={hotelImages}
                                                    alt={hotelName}
                                                    className='h-full w-full'
                                                    imageClassName='object-cover'
                                                    showNavigation={
                                                      hotelImages.length > 1
                                                    }
                                                    showPagination={
                                                      hotelImages.length > 1
                                                    }
                                                    navigationId={`preview-hotel-${index}`}
                                                    buttonSize='sm'
                                                    loop={false}
                                                  />
                                                </div>
                                                <div className='space-y-2 p-3'>
                                                  <h4 className='text-sm font-semibold text-gray-900'>
                                                    {hotelName}
                                                  </h4>
                                                  {(hotelType ||
                                                    hotelRating ||
                                                    hotelCode) && (
                                                    <div className='flex flex-wrap gap-1.5'>
                                                      {hotelType ? (
                                                        <span className='inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] text-gray-700 capitalize'>
                                                          {hotelType}
                                                        </span>
                                                      ) : null}
                                                      {hotelRating ? (
                                                        <span className='inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700'>
                                                          <span aria-hidden='true'>
                                                            ★
                                                          </span>
                                                          {hotelRating}
                                                        </span>
                                                      ) : null}
                                                      {hotelCode ? (
                                                        <span className='inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] text-gray-700'>
                                                          Code: {hotelCode}
                                                        </span>
                                                      ) : null}
                                                    </div>
                                                  )}
                                                  {locationText ? (
                                                    <p className='text-xs text-gray-600'>
                                                      {locationText}
                                                    </p>
                                                  ) : null}
                                                  {addressText ? (
                                                    <p className='text-xs text-gray-500'>
                                                      {addressText}
                                                    </p>
                                                  ) : null}
                                                  <p className='line-clamp-3 text-xs leading-5 text-gray-700'>
                                                    {hotelDescription ||
                                                      'No description available.'}
                                                  </p>
                                                  {displayAmenities.length >
                                                  0 ? (
                                                    <div className='flex flex-wrap gap-1.5 pt-1'>
                                                      {displayAmenities.map(
                                                        (
                                                          amenity,
                                                          amenityIndex
                                                        ) => (
                                                          <span
                                                            key={`${amenity}-${amenityIndex}`}
                                                            className='inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] text-gray-700'
                                                          >
                                                            {amenity}
                                                          </span>
                                                        )
                                                      )}
                                                      {remainingAmenities >
                                                      0 ? (
                                                        <span className='inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] text-gray-700'>
                                                          +{remainingAmenities}
                                                        </span>
                                                      ) : null}
                                                    </div>
                                                  ) : null}
                                                </div>
                                              </div>
                                            }
                                          />
                                        );
                                      })()
                                    : stayCell}
                                </td>
                                <td className='px-4 py-3 align-top'>
                                  {itineraryHighlights.length > 0 ? (
                                    <div className='grid grid-cols-3 gap-2'>
                                      {itineraryHighlights.map(
                                        (highlight, hIndex) => {
                                          const title =
                                            highlight.title?.trim() ||
                                            'Highlight';
                                          const imageSrc =
                                            highlight.imageUrl?.[0] ||
                                            '/assets/images/No-Image.jpg';
                                          const locationText =
                                            getHighlightLocationText(
                                              highlight.locations
                                            );
                                          const status = getHighlightStatus(
                                            highlight.isIncluded
                                          );
                                          return (
                                            <InteractivePopover
                                              key={`${highlight._id || title}-${hIndex}`}
                                              triggerClassName='inline-flex cursor-pointer'
                                              contentClassName='w-[320px] p-0 !rounded-[15px] !border-white border shadow-[0px_0px_5px_1px_rgba(0,0,0,0.15)] overflow-hidden'
                                              trigger={
                                                <div
                                                  className='h-[52px] w-[52px] overflow-hidden rounded-md border border-gray-200'
                                                  title={title}
                                                >
                                                  <img
                                                    src={imageSrc}
                                                    alt={title}
                                                    className='h-full w-full object-cover'
                                                  />
                                                </div>
                                              }
                                              content={
                                                <div className='bg-white'>
                                                  <div className='h-[140px] w-full overflow-hidden'>
                                                    <img
                                                      src={imageSrc}
                                                      alt={title}
                                                      className='h-full w-full object-cover'
                                                    />
                                                  </div>
                                                  <div className='space-y-2 p-3'>
                                                    <div className='flex items-start justify-between gap-2'>
                                                      <h4 className='text-sm font-semibold text-gray-900'>
                                                        {title}
                                                      </h4>
                                                      <span
                                                        className={`inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${status.className}`}
                                                      >
                                                        {status.label}
                                                      </span>
                                                    </div>
                                                    {locationText ? (
                                                      <p className='text-xs text-gray-600'>
                                                        {locationText}
                                                      </p>
                                                    ) : null}
                                                    <p className='text-xs leading-5 text-gray-700'>
                                                      {highlight.description?.trim() ||
                                                        'No description available.'}
                                                    </p>
                                                  </div>
                                                </div>
                                              }
                                            />
                                          );
                                        }
                                      )}
                                    </div>
                                  ) : (
                                    <span className='text-gray-500'>—</span>
                                  )}
                                </td>
                                <td className='min-w-[240px] whitespace-pre-wrap px-4 py-3 align-top text-gray-800'>
                                  {row.details}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className='text-gray-700'>—</div>
                  )
                ) : section.title === 'Inclusions' ||
                  section.title === 'Exclusions' ||
                  section.title === 'Documents Required' ||
                  section.title === 'Things to be Noted' ||
                  section.title === 'Possible Additional Costs' ? (
                  <div
                    className='prose prose-sm max-w-none leading-7 text-gray-800 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1'
                    dangerouslySetInnerHTML={{
                      __html: section.content || '<p>—</p>',
                    }}
                  />
                ) : (
                  <div className='leading-7 whitespace-pre-wrap text-gray-800'>
                    {section.content || '—'}
                  </div>
                )}
              </section>,
            ];
          })}

          {hasAgentContact ? (
            <section className='border-t border-gray-200 pt-8'>
              <h2 className='mb-3 text-xl font-semibold text-gray-900'>
                Agent Contact
              </h2>
              <div className='space-y-2 p-1.5 text-sm text-gray-800'>
                <p>
                  <span className='font-medium text-gray-600'>Name:</span>{' '}
                  {agentContact.name.trim() || '—'}
                </p>
                {agentContact.phone.trim() ? (
                  <p>
                    <span className='font-medium text-gray-600'>Phone:</span>{' '}
                    {agentContact.phone.trim()}
                  </p>
                ) : null}
                {agentContact.email.trim() ? (
                  <p>
                    <span className='font-medium text-gray-600'>Email:</span>{' '}
                    {agentContact.email.trim()}
                  </p>
                ) : null}
              </div>
            </section>
          ) : null}
        </div>

        <Dialog open={shareUrlDialogOpen} onOpenChange={setShareUrlDialogOpen}>
          <DialogContent className='sm:max-w-lg'>
            <DialogHeader>
              <DialogTitle>Preview link</DialogTitle>
              <DialogDescription>
                Use this URL when sharing (for example, WhatsApp link previews).
                Copy it again from here if needed.
              </DialogDescription>
            </DialogHeader>
            <div className='rounded-md border border-gray-200 bg-gray-50 p-3'>
              <p className='break-all font-mono text-xs leading-relaxed text-gray-900 sm:text-sm'>
                {shareUrlSnapshot || '—'}
              </p>
            </div>
            <DialogFooter className='gap-2 sm:gap-0'>
              <Button
                type='button'
                variant='outline'
                onClick={() => setShareUrlDialogOpen(false)}
              >
                Close
              </Button>
              <Button
                type='button'
                onClick={async () => {
                  const url = shareUrlSnapshot || sharePageUrl;
                  try {
                    await navigator.clipboard.writeText(url);
                    toast.success('Link copied again');
                  } catch {
                    toast.error(
                      'Copy failed. Select the text above and copy manually.'
                    );
                  }
                }}
              >
                Copy again
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
